

import { type GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { cloneDeep} from 'lodash';
import React, { ReactNode, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  renderHeaderActionButtons,
  TableDashboard,
  useAppDispatch,
  useAppSelector,
} from '../../globalUtils/globalExports';


import {
  getUsbCertificatesReducer,
  getGlobalReducer,
  setTableColumnPrefrences,
  fetchUsbCertificatesData,
  setUsbCertificatesListTableProps,
  usbCertificatesType,
} from '../../rmsReduxStore/reduxExports';
import './usbCertificates.css';
import { NoDataComponentUsbCertificates } from '../../globalUtils/TableDashboard/TableComponents';
import { downloadFile, formatRelativeTimeForTable } from '../../globalUtils/globalHooks';
import access from '../../auth/service/AccessControl';

import { useNavigate } from 'react-router-dom';
import { IconWithTooltip } from '../../globalUtils/globalIcons';
import { toast } from 'react-toastify';





const UsbCertificates = memo(function UsbCertificates() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const layouts = ['list', 'map'];
  const { usbCertificates, loader, usbCertificatesListTableProps,usbCertificatesCount } = useAppSelector(getUsbCertificatesReducer);
  const usbCertificatesListTablePropsRef = useRef(usbCertificatesListTableProps);
  const usbCertificatesRef = useRef(usbCertificates);
  const {screenTableColumnPrefrences} = useAppSelector(getGlobalReducer)

  

  useEffect(() => {
    usbCertificatesListTablePropsRef.current = usbCertificatesListTableProps;
  }, [usbCertificatesListTableProps]);

  useEffect(() => {
    usbCertificatesRef.current = usbCertificates;
  }, [usbCertificates]);

  const onChangeOfSearchText = useCallback(async (searchTerm) => {
    if (usbCertificatesListTablePropsRef?.current) {
      await layoutCallback(1, usbCertificatesListTablePropsRef?.current?.pageSize, 'list', usbCertificatesListTablePropsRef?.current?.sortBy, usbCertificatesListTablePropsRef?.current?.order, null, searchTerm)
    }
  }, [usbCertificates, usbCertificatesListTablePropsRef?.current, usbCertificatesRef?.current]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'manage-charger-parameters-search-box',
      searchFieldName: 'manage-charger-parameters-search-box',
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText]);

  // TODO: Status Icon display
  const getStatusIconClass = {
    'InProgress': 'status__in-progress__parameter__icon',
    'Scheduled': 'status__scheduled__parameter__icon',
    'Completed': 'status__completed__parameter__icon',
    'Enabled':'status__completed__parameter__icon',
  }


  const headerActionBtns = renderHeaderActionButtons({
    actionBtns:
      [
        {
          id: 'add_new_certificate',
          isAccessible: access?.updateSetting?.export(),
          headerActionBtnClick: (): void => {
            navigate('/add-usb-certificate')
          }
        }
      ]
  })






  // TODO: Table Layout Callbacks
  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['name','employeeId','mobileNumber'],
        headerRightDataConfig: {
          actionIconsComponent: true,
          statusIconComponent: true,
        },
      },
      statusIconKey: '',
      statusIconMapper: getStatusIconClass,
      contentDataConfig: [
        [
          {
            key: 'createdAt',
            iconClassName: 'parameters_createdOn_icon',
          },
        ],
      ],
    };
  }, []);

  const getTableRows = useCallback((): IUsbCertificates[] => {
    return usbCertificates !== null && usbCertificates?.length > 0 ? usbCertificates : [];
  }, [usbCertificates]);


  
  const onDownloadFileIconClick = (rowData) => {
    if(!rowData?.zipFile) {
      return toast.warn('No certificates present.');
    }
    try{
      downloadFile({ url: rowData?.zipFile });
      return toast.success('File downloaded.');
    } catch{
      return toast.error('Download failed.');
    }

}

  const actionIconsList = useCallback(()=>{
    return [
    {
        icon: 'rms__documentation__download__icon',
        tooltipText: 'Download Certificate',
        fieldId:'rms-support-documentation-download-btn',
        className:'rms-support-documentation-download__icon__wrap',
        isAccessible: true,
        action: onDownloadFileIconClick
    }
  ]
},[])
  const usbCertificatesColumns = useMemo(() => {

    return [

      {
        field: 'name',
        headerName: 'Name',
        maxWidth: 500,
        sortable: true,
        minWidth: 200,
        hideable:false,
      },
      {
        field: 'employeeId',
        headerName: 'Employee ID',
        flex: .6,
        sortable: true,
        minWidth: 80,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'mobileNumber',
        headerName: 'Mobile Number',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'createdAt',
        flex: .6,
        headerName: 'Created At',
        minWidth: 150,
        renderCell: (
          params: GridRenderCellParams<GridValidRowModel>
        ): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.createdAt);
          return formatRelativeTimeForTable(date);
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        flex: .3,
        minWidth: 20,
        sortable: false,
        pinnable: false,
        renderCell: (params) => {
          const rowData = params?.row;
          return (
            <div className='rms__support__documentation__action__icon__wrap'>
              {
                actionIconsList().map(actionBtn=>{
                  if(!actionBtn?.isAccessible)return;
                  return (
                    <div key={actionBtn.fieldId} className={actionBtn.className} onClick={()=>actionBtn.action(rowData)}>
                      <IconWithTooltip  iconClassName={actionBtn?.icon} toolTipText={actionBtn?.tooltipText}/>
                    </div>
                  )
                })
              }
            </div>
          )
        }
      }
    ];
  }, []);




  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, filterData, searchTerm) => {
    const tableProps: IChargerListTableProps = cloneDeep(usbCertificatesListTablePropsRef?.current)

    await dispatch(fetchUsbCertificatesData({
      sortBy: sortField || usbCertificatesListTablePropsRef?.current?.sortBy,
      order: sortOrder || usbCertificatesListTablePropsRef?.current?.order,
      pageSize: pageSize || 15,
      pageNumber,
      filters: null,
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : usbCertificatesListTablePropsRef?.current?.searchTerm
    }));

    if (tableProps) {
      tableProps.view = view ?? 'list'
      tableProps.sortBy = sortField || usbCertificatesListTablePropsRef?.current?.sortBy;
      tableProps.order = sortOrder || usbCertificatesListTablePropsRef?.current?.order;
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize || 15;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setUsbCertificatesListTableProps(tableProps))
  }, [usbCertificatesListTablePropsRef?.current, dispatch]);

    // Handler For Column Prefrence Chnages
    const handleColumnPrefrenceChange = useCallback(async (operation,data) => {
      await dispatch(setTableColumnPrefrences('usbCertificates',operation,data))
   },[])

  // Redux state Clean up
  const usbCertificatesListCleanUpState = useCallback(() => {
    const action = {
      type: usbCertificatesType.CLEAR_USB_CERTIFICATES_LIST_DATA
    }
    dispatch(action);
  }, [])
  useEffect(() => {
    window.addEventListener('beforeunload', usbCertificatesListCleanUpState);
    return (): void => {
      usbCertificatesListCleanUpState()
      window.removeEventListener('beforeunload', usbCertificatesListCleanUpState);
    }
  }, [])



  return (
    <>
      <TableDashboard
        tableId="manage__usb_certificates__table"
        tableClassName="manage__usb_certificates__table"
        header={'USB Certificates'}
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={usbCertificatesColumns}
        tableRows={getTableRows()}
        mobileViewConfiguration={mobileViewConfiguration}
        layouts={layouts}
        layoutView={'list'}
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={usbCertificatesCount ??0}
        showSkeleton={!usbCertificates}
        totalCountText={'certificates'}
        showLoader={loader}
        customNoDataComponent={NoDataComponentUsbCertificates}
        pageNumber={usbCertificatesListTableProps?.pageNumber}
        tablePageSize={usbCertificatesListTableProps?.pageSize}
        sortField={usbCertificatesListTableProps?.sortBy}
        sortOrder={usbCertificatesListTableProps?.order}
        referenceDataCallbacksFilters={['']}
        columnPrefrences={screenTableColumnPrefrences?.find((item) => item.screen === 'usbCertificates')}
        handleColumnPrefrenceChange={handleColumnPrefrenceChange}
      />    
    </>
  );
})

export default UsbCertificates;
